import React, { useState, useEffect } from 'react';
import { getVehicles, createVehicle, deleteVehicle, getLegalEntities } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { getStatusColor } from '../lib/utils';
import { Plus, Car, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const VEHICLE_TYPES = ['SPV', 'Fund', 'AIF Scheme', 'Mandate', 'Co-invest'];
const COMPLIANCE_PROFILES = ['Regulated', 'Unregulated', 'Charity'];
const STATUSES = ['Active', 'In Formation', 'Closed', 'Wound Up'];

const VehiclesPage = () => {
  const [vehicles, setVehicles] = useState([]);
  const [legalEntities, setLegalEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    vehicle_type: 'SPV',
    jurisdiction: '',
    manager_legal_entity_id: '',
    compliance_profile: 'Unregulated',
    status: 'Active'
  });

  const fetchData = async () => {
    try {
      const [vehiclesRes, legalRes] = await Promise.all([
        getVehicles({}),
        getLegalEntities()
      ]);
      setVehicles(vehiclesRes.data);
      setLegalEntities(legalRes.data);
    } catch (error) {
      toast.error('Failed to load vehicles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await createVehicle({
        ...formData,
        manager_legal_entity_id: formData.manager_legal_entity_id || null
      });
      toast.success('Vehicle created');
      setDialogOpen(false);
      setFormData({ name: '', vehicle_type: 'SPV', jurisdiction: '', manager_legal_entity_id: '', compliance_profile: 'Unregulated', status: 'Active' });
      fetchData();
    } catch (error) {
      toast.error('Failed to create vehicle');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this vehicle?')) return;
    try {
      await deleteVehicle(id);
      toast.success('Vehicle deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const getEntityName = (id) => legalEntities.find(e => e.id === id)?.name || '-';

  if (loading) {
    return <div className="flex items-center justify-center h-64 text-zinc-500 font-inter">Loading...</div>;
  }

  return (
    <div className="space-y-6" data-testid="vehicles-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-manrope text-xl font-bold text-zinc-900">Vehicles</h1>
          <p className="text-sm text-zinc-500 font-inter">SPVs, Funds, and Investment Mandates</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-sm" data-testid="create-vehicle-btn">
              <Plus className="w-4 h-4 mr-1" /> New Vehicle
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-sm">
            <DialogHeader>
              <DialogTitle className="font-manrope">Create Vehicle</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <Label className="font-inter text-sm">Vehicle Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="rounded-sm"
                  placeholder="VCAP Growth SPV I"
                  required
                  data-testid="vehicle-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="font-inter text-sm">Type</Label>
                  <Select value={formData.vehicle_type} onValueChange={(v) => setFormData({ ...formData, vehicle_type: v })}>
                    <SelectTrigger className="rounded-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {VEHICLE_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="font-inter text-sm">Jurisdiction</Label>
                  <Input
                    value={formData.jurisdiction}
                    onChange={(e) => setFormData({ ...formData, jurisdiction: e.target.value })}
                    className="rounded-sm"
                    placeholder="Singapore, Delaware..."
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="font-inter text-sm">Compliance Profile</Label>
                  <Select value={formData.compliance_profile} onValueChange={(v) => setFormData({ ...formData, compliance_profile: v })}>
                    <SelectTrigger className="rounded-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {COMPLIANCE_PROFILES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="font-inter text-sm">Status</Label>
                  <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                    <SelectTrigger className="rounded-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="font-inter text-sm">Manager Legal Entity</Label>
                <Select value={formData.manager_legal_entity_id || 'none'} onValueChange={(v) => setFormData({ ...formData, manager_legal_entity_id: v === 'none' ? '' : v })}>
                  <SelectTrigger className="rounded-sm">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {legalEntities.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" className="w-full rounded-sm" data-testid="submit-vehicle-btn">
                Create Vehicle
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Vehicles List */}
      {vehicles.length === 0 ? (
        <Card className="border border-zinc-200 shadow-none rounded-sm">
          <CardContent className="p-8 text-center">
            <Car className="w-8 h-8 text-zinc-300 mx-auto mb-2" />
            <p className="text-zinc-500 font-inter">No vehicles yet. Create one to start tracking.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {vehicles.map(vehicle => (
            <Card key={vehicle.id} className="border border-zinc-200 shadow-none rounded-sm hover:border-zinc-300 transition-colors">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-medium font-inter text-zinc-900">{vehicle.name}</h3>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge variant="outline" className="text-xs">{vehicle.vehicle_type}</Badge>
                      <Badge className={getStatusColor(vehicle.status)}>{vehicle.status}</Badge>
                    </div>
                    <div className="mt-3 text-xs text-zinc-500 font-mono space-y-1">
                      <p>Jurisdiction: {vehicle.jurisdiction}</p>
                      <p>Compliance: {vehicle.compliance_profile}</p>
                      <p>Manager: {getEntityName(vehicle.manager_legal_entity_id)}</p>
                    </div>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="text-zinc-400 hover:text-red-500"
                    onClick={() => handleDelete(vehicle.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default VehiclesPage;
